import { useMemo } from "react";

import {
  type ActionItem,
  useListActionItemsForEncounter,
} from "@elation/api-client/src/stitch-web";

import {
  ACTION_ITEM_SECTIONS,
  ActionItemEntity,
  type ActionItemListArtifact,
  type ActionItemSection,
  useActionItemMutations,
} from "@src/features/action-items";
import { Checkbox } from "@src/shared/components/ui/checkbox";
import { CollapsiblePanel } from "@src/shared/components/v2/CollapsiblePanel";
import { GeneratedArtifactPanelState } from "@src/shared/components/v2/GeneratedArtifactPanelState";
import type { GeneratedArtifactViewState } from "@src/shared/domain/generated-artifact";
import { cn } from "@src/shared/lib/utils";

export interface CompletedVisitActionItemsPanelProps {
  encounterId?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** False when the panel owns a whole tab and has nothing to collapse into. */
  collapsible?: boolean;
}

export interface CompletedVisitActionItemsPanelBodyProps {
  items: ActionItem[];
  viewState?: GeneratedArtifactViewState;
  onToggle?: (item: ActionItem) => void;
}

function useCompletedVisitActionItems({
  encounterId,
}: {
  encounterId?: number;
}) {
  const actionItemsQuery = useListActionItemsForEncounter(encounterId ?? 0, {
    query: {
      enabled: encounterId != null,
    },
  });
  const response = actionItemsQuery.data?.data as
    | ActionItemListArtifact
    | undefined;
  const items = useMemo(
    () =>
      [...(response?.items ?? [])].sort(ActionItemEntity.compareByCreatedAtAsc),
    [response?.items],
  );

  const viewState = ActionItemEntity.resolveViewState({
    itemCount: items.length,
    isInitialLoading: actionItemsQuery.isPending && !actionItemsQuery.data,
    isFetchError: actionItemsQuery.isError && items.length === 0,
  });

  return {
    items,
    viewState,
  };
}

export function CompletedVisitActionItemsPanel({
  encounterId,
  open,
  defaultOpen = false,
  onOpenChange,
  collapsible = true,
}: CompletedVisitActionItemsPanelProps) {
  const { items, viewState } = useCompletedVisitActionItems({
    encounterId,
  });
  const { toggleActionItem } = useActionItemMutations(encounterId ?? 0);

  return (
    <CollapsiblePanel
      label="Action items"
      count={String(items.length)}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      collapsible={collapsible}
      className="h-full"
      contentClassName="min-h-0 overflow-auto"
    >
      <CompletedVisitActionItemsPanelBody
        items={items}
        viewState={viewState}
        onToggle={encounterId != null ? toggleActionItem : undefined}
      />
    </CollapsiblePanel>
  );
}

export function CompletedVisitActionItemsPanelBody({
  items,
  viewState = "ready",
  onToggle,
}: CompletedVisitActionItemsPanelBodyProps) {
  const groupedSections = useMemo(() => {
    const groups = ActionItemEntity.groupBySection(items);
    return ACTION_ITEM_SECTIONS.flatMap((section) => {
      const sectionItems = groups[section];
      return sectionItems.length > 0
        ? ([[section, sectionItems]] as const)
        : [];
    }) as Array<[ActionItemSection, ActionItem[]]>;
  }, [items]);

  return (
    <GeneratedArtifactPanelState
      viewState={
        viewState === "ready" && items.length === 0 ? "empty" : viewState
      }
      generatingLabel="Loading action items"
      emptyMessage="No action items were generated for this visit."
      failedMessage="Action items unavailable for this visit."
      errorFallback="Unable to load action items."
    >
      <div className="divide-y divide-border">
        {groupedSections.map(([section, sectionItems]) => (
          <section key={section}>
            <div className="flex items-center justify-between bg-surface px-3 py-2">
              <h3 className="font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                {section}
              </h3>
              <span className="font-mono text-[10px] text-muted-foreground">
                {sectionItems.length}
              </span>
            </div>
            <ul
              className="divide-y divide-border"
              aria-label={`${section} action items`}
            >
              {sectionItems.map((item) => {
                const isCompleted = ActionItemEntity.isCompleted(item);
                const categoryLabel = ActionItemEntity.categoryLabel(item);
                const canToggle = onToggle != null;

                return (
                  <li
                    key={item.id}
                    className="flex min-h-11 items-start gap-3 px-3 py-2"
                  >
                    <Checkbox
                      aria-label={`Mark ${item.summary} ${
                        isCompleted ? "incomplete" : "complete"
                      }`}
                      checked={isCompleted}
                      className="mt-0.5 size-[17px] shrink-0 overflow-hidden rounded-full border-muted-foreground/40 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 [&_svg]:size-3 data-[state=checked]:border-success data-[state=checked]:bg-success data-[state=checked]:text-success-foreground"
                      disabled={!canToggle}
                      onCheckedChange={() => onToggle?.(item)}
                    />
                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          "text-[13px] leading-5 text-foreground",
                          isCompleted &&
                            "text-muted-foreground line-through decoration-muted-foreground/60",
                        )}
                      >
                        {item.summary}
                      </p>
                      {categoryLabel ? (
                        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                          {categoryLabel}
                        </p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </GeneratedArtifactPanelState>
  );
}
